import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { readEndpoint} from "redux-json-api";
import Fab from '@material-ui/core/Fab';
import red from '@material-ui/core/es/colors/red';
import Business from '@material-ui/icons/Business';
import DirectionsBus from '@material-ui/icons/DirectionsBus';
import Edit from '@material-ui/icons/Edit';
import Person from '@material-ui/icons/Person';
import { withStyles } from '@material-ui/styles';

import { detailOpen } from '../../modules/actions/rideAdmin';

import {
  FlexTableRow,
  FabTableCell, MileageTableCell, RideTableCell, RouteBlockageTableCell,
} from './TableTools';
import {
  makeGetCompanyFromRide,
  makeGetDriverFromRide,
  makeGetRelatedStopsFromRouteBlockage,
  makeGetRouteBlockageFromRide,
  makeGetRouteBlockageNameFromRide,
  makeGetVehicleFromRide,
} from './selectors';

import './RideRow.scss';
import { path } from "ramda";

const RideRow = ({
  classes,
  company,
  detailOpen,
  driver,
  ride,
  relatedStops,
  routeBlockage,
  routeBlockageName,
  vehicle,
  readEndpoint,
}) => {
  useEffect(() => {
    if (routeBlockage) {
      const railwayId = path(['relationships', 'railway', 'data', 'id'], routeBlockage);
      readEndpoint(`railway/${railwayId}`)
    }
  }, []);

  return (
    <FlexTableRow>
      <RouteBlockageTableCell>
        {routeBlockageName}
        <br/>
        {(new Date(routeBlockage.attributes['start-at'])).toString()}
      </RouteBlockageTableCell>

      <RideTableCell>
      <span>
        <Business className={classes.symbols}/>
        {company.attributes.name}
      </span>
        <br/>
        <span>
        <Person className={classes.symbols}/>
          {`${driver.attributes['first-name']} ${driver.attributes['last-name']}`}
      </span>
        <br/>
        <span>
        <DirectionsBus className={classes.symbols}/>
          {`${vehicle.attributes['vehicle-number']} / ${vehicle.attributes['license-plate']}`}
      </span>
      </RideTableCell>

      <MileageTableCell>
        <ul className="rideadmin-riderow-mileage-list">
          <li>
            <span>1/4: </span>
            <span>{ride.attributes['distance-depot-start'] || '--'}</span>
            <span> km</span>
          </li>

          <li>
            <span>2/4: </span>
            <span>{ride.attributes['distance-shuttle-start'] || '--'}</span>
            <span> km</span>
          </li>

          <li>
            <span>3/4: </span>
            <span>{ride.attributes['distance-shuttle-end'] || '--'}</span>
            <span> km</span>
          </li>

          <li>
            <span>4/4: </span>
            <span>{ride.attributes['distance-depot-end'] || '--'}</span>
            <span> km</span>
          </li>
        </ul>
      </MileageTableCell>

      <FabTableCell>
        <div onClick={() => detailOpen({company, driver, ride, relatedStops, routeBlockage, routeBlockageName, vehicle})}
             style={{width: 40}}>
          <Fab color="primary" aria-label="edit" size="small">
            <Edit/>
          </Fab>
        </div>
      </FabTableCell>
    </FlexTableRow>
  );
};

RideRow.propTypes = {
  // own props
  ride: PropTypes.shape({
    id: PropTypes.string.isRequired,

    attributes: PropTypes.shape({
      'company-id': PropTypes.number.isRequired,
      'distance-depot-start': PropTypes.number,
      'distance-shuttle-start': PropTypes.number,
      'distance-shuttle-end': PropTypes.number,
      'distance-depot-end': PropTypes.number,
    }).isRequired,

    relationships: PropTypes.shape({
      driver: PropTypes.shape({
        data: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
      }).isRequired,

      vehicle: PropTypes.shape({
        data: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,

  // state props
  company: PropTypes.shape({
    attributes: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
  }),

  driver: PropTypes.shape({
    attributes: PropTypes.shape({
      'first-name': PropTypes.string.isRequired,
      'last-name': PropTypes.string.isRequired,
    }).isRequired,
  }),

  relatedStops: PropTypes.arrayOf(PropTypes.shape({
    'id': PropTypes.string.isRequired,
    'name': PropTypes.string.isRequired,
  })).isRequired,

  routeBlockage: PropTypes.shape({
    attributes: PropTypes.shape({
      'start-at': PropTypes.string.isRequired,
    }).isRequired,

    relationships: PropTypes.shape({
      'stop-from': PropTypes.shape({
        data: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,

      'stop-to': PropTypes.shape({
        data: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
  }),

  routeBlockageName: PropTypes.string.isRequired,

  vehicle: PropTypes.shape({
    attributes: PropTypes.shape({
      'license-plate': PropTypes.string.isRequired,
      'vehicle-number': PropTypes.number.isRequired,
    }).isRequired,
  }),

  // dispatch props
  detailOpen: PropTypes.func.isRequired,
};

RideRow.defaultProps = {
  company: { attributes: { name: 'Onbekend' } },
  driver: { attributes: { 'first-name': 'Onbekend', 'last-name': 'Onbekend' } },
  routeBlockage: {
    attributes: { 'start-at': '' },
    relationships: {
      'stop-from': { data: { id: '' } },
      'stop-to': { data: { id: '' } },
    },
  },
  vehicle: { attributes: { 'license-plate': '--', 'vehicle-number': 0 } },
};

const makeMapStateToProps = () => {
  const getCompanyFromRide = makeGetCompanyFromRide();
  const getDriverFromRide = makeGetDriverFromRide();
  const getRelatedStops = makeGetRelatedStopsFromRouteBlockage();
  const getRouteBlockageFromRide = makeGetRouteBlockageFromRide();
  const getRouteBlockageNameFromRide = makeGetRouteBlockageNameFromRide();
  const getVehicleFromRide = makeGetVehicleFromRide();

  return (state, ownProps) => ({
    company: getCompanyFromRide(state, ownProps),
    driver: getDriverFromRide(state, ownProps),
    relatedStops: getRelatedStops(state, ownProps),
    routeBlockage: getRouteBlockageFromRide(state, ownProps),
    routeBlockageName: getRouteBlockageNameFromRide(state, ownProps),
    vehicle: getVehicleFromRide(state, ownProps),
  });
};

export default connect(makeMapStateToProps, { detailOpen, readEndpoint })(withStyles({
  tableRowColumn: {
    paddingLeft: '6px',
    paddingRight: '6px',
  },
  symbols: {
    color: red[800],
    fill: red[800],
    height: 13,
    width: 13,
    verticalAlign: 'middle',
    margin: '1px 0.5em 1px 0px',
  },

  mileageField: {
    fontSize: '13px',
    lineHeight: '16px',
    height: '20px',
    width: '100px',
  },

  mileageFieldHint: {
    bottom: '0px',
  },

  mileageFieldUnderline: {
    bottom: '0px',
  },

  mileageActionButton: {
    margin: '10px',
  },
})(RideRow));
